import {debounce} from '@github/mini-throttle'
import {type InteractiveMarkdownViewerProps, NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {SanitizedHTML} from '@github-ui/safe-html'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import React, {useCallback, useLayoutEffect, useMemo, useRef} from 'react'

import {LABELS} from '../../constants/labels'
import {TEST_IDS} from '../../constants/test-ids'
import styles from './IssueMarkdownViewer.module.css'

type IssueMarkdownViewerProps = {
  createdViaEmail?: boolean
  dataTestId?: string
  html: SanitizedHTML
  issueCommentBodyRef?: React.RefObject<HTMLDivElement | null>
  markdown: string
  onSave: (newBody: string, onCompleted: () => void, onError: () => void) => void
  /**
   * Called when the user clicks a link element. This can be used to intercept the click
   * and provide custom routing.
   *
   * Note that this is a native HTML `MouseEvent` and not a `React.ClickEvent`.
   */
  onLinkClick?: (event: MouseEvent) => void
  onConvertToIssue?: InteractiveMarkdownViewerProps['onConvertToIssue']
  onConvertToSubIssue?: InteractiveMarkdownViewerProps['onConvertToSubIssue']
  viewerCanCreateIssues: boolean
  viewerCanUpdate: boolean
}

export const IssueMarkdownViewer = ({
  createdViaEmail = false,
  dataTestId = TEST_IDS.markdownBody,
  html,
  issueCommentBodyRef,
  markdown,
  onConvertToIssue,
  onConvertToSubIssue,
  onLinkClick,
  onSave,
  viewerCanCreateIssues,
  viewerCanUpdate,
}: IssueMarkdownViewerProps) => {
  const {addToast} = useToastContext()
  const [isSaving, setIsSaving] = React.useState(false)

  const finishEditing = useCallback(
    (onSuccess: () => void, onError: () => void, error?: string) => {
      setIsSaving(false)
      if (error) {
        onError()

        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: error,
        })
      } else {
        onSuccess()
      }
    },
    [addToast],
  )

  const onPerformChange = useCallback(
    (body: string) => {
      const promise = new Promise<void>((resolve, reject) => {
        setIsSaving(true)
        onSave(
          body,
          () => finishEditing(resolve, reject),
          () => finishEditing(resolve, reject, 'Could not update issue comment'),
        )
      })

      return promise
    },
    [finishEditing, onSave],
  )

  // useRef is used here to make sure we don't call onPerformChange with an outdated function
  // in case the issue has changed for example in between
  const onPerformChangeRef = useRef(onPerformChange)
  useLayoutEffect(() => {
    onPerformChangeRef.current = onPerformChange
  }, [onPerformChange])

  // eslint-disable-next-line react-hooks/refs
  const onChange = useMemo(() => debounce((nextValue: string) => onPerformChangeRef.current(nextValue), 500), [])

  const innerBody: React.ReactElement = useMemo(() => {
    if (String(html) !== '') {
      return (
        <NewMarkdownViewer
          className={createdViaEmail ? 'email-format' : undefined}
          disabled={!viewerCanUpdate || isSaving}
          markdownValue={markdown}
          onChange={onChange}
          onConvertToIssue={onConvertToIssue}
          onConvertToSubIssue={onConvertToSubIssue}
          onLinkClick={onLinkClick}
          verifiedHTML={html}
          viewerCanCreateIssues={viewerCanCreateIssues}
          viewerCanUpdate={viewerCanUpdate}
        />
      )
    } else {
      return <span className={styles.MarkdownViewerHelpText}>{LABELS.noDescriptionProvided}</span>
    }
  }, [
    createdViaEmail,
    html,
    isSaving,
    markdown,
    onChange,
    onConvertToIssue,
    onConvertToSubIssue,
    onLinkClick,
    viewerCanCreateIssues,
    viewerCanUpdate,
  ])

  return (
    <div
      data-testid={dataTestId}
      data-team-hovercards-enabled
      className="markdown-body"
      // Make links from project -> project hard navigate if the user chooses to open in same tab
      // https://github.com/github/memex/issues/9202#issuecomment-1085941126
      data-turbolinks="false"
      ref={issueCommentBodyRef}
    >
      {innerBody}
    </div>
  )
}
